import React from 'react';
import { Field, Form, Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';

interface OdooConfigurationEditFormProps {
  onSubmit: (values: {
    url: string;
    db: string;
    name: string;
    username: string;
    password: string;
  }) => Promise<void>;
  onCancel: () => void;
  initialValues: {
    url: string;
    db: string;
    name: string;
    username: string;
    password?: string;
  };
}

const OdooConfigurationSchema = Yup.object().shape({
  url: Yup.string().required('URL is required').url('Must be a valid URL'),
  db: Yup.string().required('Database name is required'),
  name: Yup.string().required('Name is required'),
  username: Yup.string().required('Username is required'),
  password: Yup.string().required('Password is required'),
});

export const OdooConfigurationEditForm: React.FC<
  OdooConfigurationEditFormProps
> = ({ onSubmit, onCancel, initialValues }) => {
  return (
    <Formik
      initialValues={{
        url: initialValues.url,
        db: initialValues.db,
        name: initialValues.name,
        username: initialValues.username,
        password: initialValues.password || '',
      }}
      validationSchema={OdooConfigurationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await onSubmit(values);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting }) => (
        <Form className="space-y-8 bg-white dark:bg-gray-800 px-6 py-8 rounded-lg shadow-md w-full transition-colors">
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white pb-2 border-b dark:border-gray-700">
              Odoo Configuration
            </h3>
            <div className="grid grid-cols-1 gap-y-6 gap-x-6 sm:grid-cols-2">
              <div>
                <label htmlFor="url" className="form-label">
                  Odoo URL *
                </label>
                <Field
                  type="text"
                  name="url"
                  className="form-input"
                  placeholder="https://odoo.example.com"
                />
                <ErrorMessage
                  name="url"
                  component="div"
                  className="text-red-500 text-sm mt-1"
                />
              </div>

              <div>
                <label htmlFor="db" className="form-label">
                  Database Name *
                </label>
                <Field
                  type="text"
                  name="db"
                  className="form-input"
                  placeholder="database_name"
                />
                <ErrorMessage
                  name="db"
                  component="div"
                  className="text-red-500 text-sm mt-1"
                />
              </div>

              <div>
                <label htmlFor="name" className="form-label">
                  Name *
                </label>
                <Field
                  type="text"
                  name="name"
                  className="form-input"
                  placeholder="Odoo Configuration"
                />
                <ErrorMessage
                  name="name"
                  component="div"
                  className="text-red-500 text-sm mt-1"
                />
              </div>

              <div>
                <label htmlFor="username" className="form-label">
                  Username *
                </label>
                <Field
                  type="text"
                  name="username"
                  className="form-input"
                  placeholder="admin"
                />
                <ErrorMessage
                  name="username"
                  component="div"
                  className="text-red-500 text-sm mt-1"
                />
              </div>

              <div>
                <label htmlFor="password" className="form-label">
                  Password *
                </label>
                <Field
                  type="text"
                  name="password"
                  className="form-input"
                  placeholder="Enter password"
                />

                <ErrorMessage
                  name="password"
                  component="div"
                  className="text-red-500 text-sm mt-1"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button type="button" onClick={onCancel} className="btn-secondary">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary"
            >
              {isSubmitting ? 'Updating...' : 'Update'}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};
